import { HotColumn, HotTable } from '@handsontable/react';
import Handsontable from 'handsontable';
import React from 'react';
import * as constants from '../constants';
import {
  alignColumns,
  changeHandler,
  defineTooltipPosition,
  getNewlyEmptyRows,
  getValidator,
  ignoreEmptyChanges,
  injectCellErrorMessage,
  trimClipboardWhitespace,
  unformatNumbers,
  validateRowsWithData,
} from '../helpers';
import { AfterChangeCallbackType, DataType, HotTableType } from '../types';
import { SpreadsheetTableNotes } from './SpreadsheetNotes';
import { SpreadsheetTableWrapper } from './SpreadsheetTableWrapper';
import type { SpreadsheetV2Props } from '../SpreadsheetV2';
import { useCustomSpreadsheetErrors } from '../hooks/useCustomSpreadsheetErrors';

interface SpreadsheetTableProps {
  hotTableRef: React.RefObject<HotTableType>;
  tableContainerRef: React.RefObject<HTMLDivElement>;
  spreadsheetWrapperRef: React.RefObject<HTMLDivElement>;
  afterSelectionEnd: ([row, column, row2, column2]: [number, number, number, number]) => void;
  id: string;
  data: DataType;
  errors?: SpreadsheetV2Props['errors'];
  columns: SpreadsheetV2Props['columns'];
  collapseClipboardWhitespace: SpreadsheetV2Props['collapseClipboardWhitespace'];
  afterChange: AfterChangeCallbackType;
  validateOnLoad?: SpreadsheetV2Props['validateOnLoad'];
  settings?: SpreadsheetV2Props['settings'];
  'data-testid'?: string | number;
}

if (typeof window !== 'undefined') {
  import('handsontable/cellTypes').then(defExp => {
    defExp.registerAllCellTypes();
  });
  import('handsontable/registry').then(defExport => {
    /*
      Note: this might not be necessary, but at the moment of writing this
      I have no idea what kinds of modules we should or shouldn't enable.
      This registers all modules, described here:
      https://handsontable.com/docs/javascript-data-grid/modules/
    */
    defExport.registerAllModules();
  });

  Promise.all([import('./renderers'), import('handsontable/renderers')]).then(
    ([{ decimalRenderer }, { registerRenderer }]) => {
      registerRenderer('decimal-percent', decimalRenderer);
    },
  );
}

const SpreadsheetTable = (props: SpreadsheetTableProps) => {
  const {
    id,
    columns,
    data,
    errors,
    afterChange,
    hotTableRef,
    afterSelectionEnd,
    tableContainerRef,
    settings,
    collapseClipboardWhitespace = false,
    'data-testid': testid,
    validateOnLoad,
    spreadsheetWrapperRef,
  } = props;
  const hasRequiredCol = React.useMemo(() => columns.some(col => col.required), [columns]);

  useCustomSpreadsheetErrors(hotTableRef, errors);

  return (
    <SpreadsheetTableWrapper ref={props.tableContainerRef} data-testid={testid}>
      {React.useMemo(
        () => (
          <HotTable
            id={id}
            ref={hotTableRef}
            afterSelectionEnd={(row, column, row2, column2) => afterSelectionEnd([row, column, row2, column2])}
            licenseKey="44698-320ca-f5a01-04b13-af817"
            data={data}
            beforeRefreshDimensions={() => {
              /*
                refreshDimensions should not run if Slider is currently animating
              */
              if (spreadsheetWrapperRef.current?.dataset.animating) {
                return false;
              }
              return undefined;
            }}
            afterChange={function afterChangeHandler(changes) {
              const self = this as Handsontable;

              /* Run validation on newly emptied rows to clear stale error states */
              self.validateRows(getNewlyEmptyRows(self, changes));

              self.batchRender(function render() {
                changeHandler(data, self, afterChange)(changes);
              });
            }}
            afterInit={function afterInit() {
              const self = this as Handsontable;
              if (validateOnLoad) {
                /*
                  This callback is passed to validateRowsWithData in order to force a re-render
                  of the parent component, SpreadsheetV2. This is necessary because the error
                  slider needs the count of invalid cells, which is only available after the
                  validation is complete.
                */
                validateRowsWithData(data, self, () => afterSelectionEnd([0, 0, 0, 0]));
              }
            }}
            /*
              Note: this function runs after _each_ cell validation,
              as opposed to after _all_ cells have been validated.
            */
            afterValidate={function afterValidate(isValid, value, row, prop, source) {
              const self = this as Handsontable;
              return injectCellErrorMessage(columns, self)!(isValid, value, row, prop, source);
            }}
            invalidCellClassName="invalid-cell"
            /*
              Whenever a column is manually resized, if the width of the column will be made
              smaller than the minimum width, the minimum width will be enforced.
            */
            modifyColWidth={function modifyColWidth(width, col) {
              const columnType = columns[col].type;
              const columnMinimumWidth = constants.defaultColumnSettings[columnType].width;

              if (columnMinimumWidth && width < columnMinimumWidth) {
                return columnMinimumWidth;
              }

              return width;
            }}
            settings={{
              minRows: 50,
              rowHeaders: true,
              stretchH: 'last',
              ...settings,
              afterGetColHeader: alignColumns(columns),
              autoColumnSize: false,
              beforeChange: function beforeChange(changes, source) {
                const self = this as Handsontable;
                ignoreEmptyChanges(self)!(changes, source);
                unformatNumbers(changes, columns);
              },
              beforePaste: function beforePaste(data, coords) {
                trimClipboardWhitespace(data, { collapseClipboardWhitespace });
              },
              beforeOnCellMouseOver: function beforeOnCellMouseOver(event, coords, TD, controller) {
                const self = this as Handsontable;
                defineTooltipPosition(self)!(event, coords, TD, controller);
              },
              columnHeaderHeight: constants.rowHeight,
              height: hasRequiredCol ? 'calc(100% - 30px)' : '100%',
              manualColumnResize: true,
              outsideClickDeselects: e => !tableContainerRef.current?.contains(e),
              readOnlyCellClassName: 'read-only',
              rowHeights: constants.rowHeight,
              selectionMode: 'range',
              viewportColumnRenderingOffset: 50,
              viewportRowRenderingOffset: 50,
              width: '100%',
            }}
          >
            {columns.map(column => {
              const { type = 'text', align = 'left', ...otherColumnProps } = column;

              if (!constants.ALLOWED_COLUMN_TYPES.includes(type)) {
                throw new Error(
                  `Invalid column type "${type}", allowed options: ${constants.ALLOWED_COLUMN_TYPES.join(', ')}.`,
                );
              }

              let className: string | undefined;

              if (column.align === 'right' && column.type === 'text') {
                className = 'htRight';
              }

              const columnHeader = otherColumnProps.required ? `* ${otherColumnProps.title}` : otherColumnProps.title;
              return (
                <HotColumn
                  className={className}
                  {...constants.defaultColumnSettings[type]}
                  {...otherColumnProps}
                  allowEmpty={!otherColumnProps.required}
                  key={`${otherColumnProps.id}-${otherColumnProps.title}`}
                  title={columnHeader}
                  validator={getValidator(column, Handsontable.validators)}
                />
              );
            })}
          </HotTable>
        ),
        [
          afterChange,
          afterSelectionEnd,
          collapseClipboardWhitespace,
          columns,
          data,
          hasRequiredCol,
          hotTableRef,
          id,
          settings,
          spreadsheetWrapperRef,
          tableContainerRef,
          validateOnLoad,
        ],
      )}
      {hasRequiredCol && <SpreadsheetTableNotes>* required</SpreadsheetTableNotes>}
    </SpreadsheetTableWrapper>
  );
};

export default SpreadsheetTable;
